import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography,
} from '@mui/material';
import { useNotification } from '@refinedev/core';
import { getClusterConfig, putClusterBranding, type ClusterConfig } from '../../lib/cluster-api';
import { uploadOrDataUrl } from '../../lib/files-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

// Hero images are capped at 5 MB — checked here so an oversized file is
// rejected before any upload (and before it becomes a multi-MB data URL).
const MAX_HERO_BYTES = 5 * 1024 * 1024;

export function ResidentAppBrandingPage() {
  const t = useT();
  const { open } = useNotification();
  const [config, setConfig] = useState<ClusterConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setConfig(await getClusterConfig());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_HERO_BYTES) {
      setError(t('brand.tooBig'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const url = await uploadOrDataUrl(file);
      setConfig(await putClusterBranding(url));
      open?.({ type: 'success', message: t('brand.updated'), key: 'hero' });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setBusy(true);
    setError(null);
    try {
      setConfig(await putClusterBranding(null));
      open?.({ type: 'success', message: t('brand.reverted'), key: 'hero' });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', py: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('brand.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('brand.subtitle', { name: config?.name ?? '' })}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  width: '100%', aspectRatio: '16 / 10', borderRadius: 1, overflow: 'hidden',
                  bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {config?.hero_image_url ? (
                  <img
                    src={config.hero_image_url}
                    alt="Hero"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('brand.useDefault')}
                  </Typography>
                )}
              </Box>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={onPick}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy} onClick={() => fileRef.current?.click()}>
                  {busy ? t('brand.working') : t('brand.upload')}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={busy || !config?.hero_image_url}
                  onClick={onRemove}
                >
                  {t('brand.remove')}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
